import React, { useEffect, useMemo, useState } from "react";
import packageInfo from "../../../package.json";
import MarkdownBlock from "../common/ChangelogMarkdown";
import { ChevronDown, ChevronRight, Download, Search } from "lucide-react";
import {
  isUnreleasedForUser,
  normalizeVersion,
  parseChangelog,
  resolveVersionState,
} from "../../utils/changelogVersions";
import { buildInstallRows } from "../../utils/installVersions";
import { getNativeAppInfo } from "../../platform/native";
import { getDevices, getFirmwareReleases } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  checkForUpdate,
  requestUpdate,
} from "../../serviceWorkerRegistration";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const CHANGELOG_URL = "/changelog.md";

function ReleaseEntry({
  release,
  isCurrent,
  isNew,
  isExpanded,
  onToggle,
  searchQuery,
}) {
  return (
    <div className="border-b border-border/40 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="group flex w-full items-center justify-between gap-4 px-2 py-4 text-left hover:bg-muted/30 rounded-md transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[15px] font-semibold text-foreground">
              {release.version === "Unreleased"
                ? release.version
                : `v${release.version}`}
            </span>
            {isCurrent && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                Current
              </span>
            )}
            {/* Released, but not in the build you are running — the shell is
                held back until you take the update. */}
            {isNew && (
              <span className="rounded-full bg-secondary/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary-foreground">
                New
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{release.date}</p>
        </div>

        {isExpanded ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
        ) : (
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
        )}
      </button>

      {isExpanded && (
        <div className="px-2 pb-6 pt-2 animate-in fade-in slide-in-from-top-1 duration-200">
          <MarkdownBlock content={release.content} searchQuery={searchQuery} />
        </div>
      )}
    </div>
  );
}

// What this person is running, pinned under the release list: the website
// build, the Android app (only inside it) and each linked timer's firmware.
function InstallVersions({ rows }) {
  return (
    <div className="shrink-0 border-t border-border/60 bg-muted/10 px-6 py-3">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        This install
      </p>
      <dl className="space-y-1 text-sm">
        {rows.map((row) => (
          <div key={row.key} className="flex items-baseline justify-between gap-3">
            <dt className="min-w-0 truncate text-muted-foreground">{row.label}</dt>
            <dd className="shrink-0 text-right">
              <span className="font-mono font-semibold text-foreground">
                v{row.version}
              </span>
              {row.note && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {row.note}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function useInstallRows(open) {
  const { isAuthenticated } = useAuth();
  const [native, setNative] = useState(null);
  const [devices, setDevices] = useState([]);
  const [releases, setReleases] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getNativeAppInfo().then((info) => {
      if (!cancelled) setNative(info);
    });
    if (isAuthenticated) {
      // Timers are a nicety here: a failure just leaves their rows out.
      Promise.all([
        getDevices().catch(() => null),
        getFirmwareReleases().catch(() => null),
      ]).then(([deviceList, releaseData]) => {
        if (cancelled) return;
        setDevices(Array.isArray(deviceList) ? deviceList : []);
        setReleases(releaseData?.releases || []);
      });
    } else {
      setDevices([]);
    }
    return () => {
      cancelled = true;
    };
  }, [open, isAuthenticated]);

  return useMemo(
    () => buildInstallRows({ web: packageInfo.version, native, devices, releases }),
    [native, devices, releases],
  );
}

export default function ChangelogModal({ open, onOpenChange }) {
  const installRows = useInstallRows(open);
  const [rawChangelog, setRawChangelog] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    const abortController = new AbortController();

    async function loadChangelog() {
      setStatus("loading");
      setErrorMessage("");

      try {
        const response = await fetch(
          `${CHANGELOG_URL}?v=${encodeURIComponent(packageInfo.version)}`,
          {
            cache: "no-store",
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Unable to load changelog (${response.status})`);
        }

        const changelog = await response.text();
        setRawChangelog(changelog);
        setStatus("ready");
      } catch (error) {
        if (error.name === "AbortError") return;
        setStatus("error");
        setErrorMessage(error.message || "Unable to load changelog.");
      }
    }

    loadChangelog();
    // Opening the changelog is the moment to look for a new build — otherwise
    // the browser only re-checks on tab focus, and the banner below would
    // advertise a release the app had not yet noticed was installable.
    checkForUpdate();

    return () => abortController.abort();
  }, [open]);

  const allReleases = useMemo(
    () => parseChangelog(rawChangelog),
    [rawChangelog],
  );

  const versionState = useMemo(
    () => resolveVersionState(allReleases, packageInfo.version),
    [allReleases],
  );

  const filteredReleases = useMemo(() => {
    if (!searchQuery.trim()) return allReleases;
    const lowerQuery = searchQuery.toLowerCase();
    return allReleases.filter(
      (r) =>
        r.version.toLowerCase().includes(lowerQuery) ||
        r.content.toLowerCase().includes(lowerQuery),
    );
  }, [allReleases, searchQuery]);

  // Open the NEWEST release, not the one you are running. You come here to read
  // what changed, and when a deploy is waiting the entry worth reading is the
  // one you do not have yet.
  useEffect(() => {
    if (!allReleases.length) {
      setExpanded({});
      return;
    }

    const target = versionState.expandVersion;
    const nextExpanded = {};
    allReleases.forEach((release) => {
      nextExpanded[release.version] =
        normalizeVersion(release.version) === normalizeVersion(target);
    });

    setExpanded(nextExpanded);
  }, [allReleases, versionState.expandVersion]);

  function toggleRelease(version) {
    setExpanded((previous) => ({
      ...previous,
      [version]: !previous[version],
    }));
  }

  const allFilteredExpanded =
    filteredReleases.length > 0 &&
    filteredReleases.every((r) => expanded[r.version]);

  function toggleAll() {
    const nextState = {};
    filteredReleases.forEach((r) => {
      nextState[r.version] = !allFilteredExpanded;
    });
    setExpanded((prev) => ({ ...prev, ...nextState }));
  }

  // Reset search when modal closes
  useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh] sm:max-w-2xl bg-background/95 backdrop-blur-sm">
        <DialogHeader className="shrink-0 border-b border-border/60 bg-background/50 px-6 py-5">
          {/* pr-6 keeps "Expand all" clear of the close button, which
              DialogContent pins at `absolute right-4 top-4` — the header's own
              px-6 insets by 24px but the X occupies 16-32px in from the edge,
              so the two overlapped. Same clearance SessionHistory uses. */}
          <div className="flex items-start justify-between gap-4 pr-6">
            <div>
              <DialogTitle className="text-xl">Changelog</DialogTitle>
              <DialogDescription className="mt-1">
                What changed in Khronos AI, with the version you are running
                highlighted.
              </DialogDescription>
            </div>

            {status === "ready" && filteredReleases.length > 0 && (
              <button
                onClick={toggleAll}
                className="shrink-0 mt-1 rounded-md px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                {allFilteredExpanded ? "Collapse all" : "Expand all"}
              </button>
            )}
          </div>

          {status === "ready" && (
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search updates, features, or fixes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-input/60 bg-background/50 py-2 pl-9 pr-4 text-sm shadow-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all"
              />
            </div>
          )}
        </DialogHeader>

        {/* The changelog is fetched live but the version badge is compiled into
            the bundle, so a deployed release shows up here while the app keeps
            running the old shell (service-worker.js holds it until asked). That
            gap is what made the menu look out of date — so name both figures,
            and make this the place the update can be taken. */}
        {status === "ready" && versionState.updateAvailable && (
          <div className="shrink-0 flex items-center justify-between gap-3 border-b border-border/60 bg-primary/5 px-6 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {`v${versionState.latest} is available`}
              </p>
              <p className="text-xs text-muted-foreground">
                {`You're running v${versionState.running}`}
              </p>
            </div>
            <button
              type="button"
              onClick={requestUpdate}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Download className="h-3.5 w-3.5" />
              Reload
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-4 py-2 sm:px-6">
          {status === "loading" && (
            <div className="mt-4 rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground animate-pulse">
              Loading changelog...
            </div>
          )}

          {status === "error" && (
            <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-4 text-sm text-destructive">
              {errorMessage}
            </div>
          )}

          {status === "ready" && filteredReleases.length === 0 && (
            <div className="mt-4 rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
              {searchQuery
                ? "No matching releases found for your search."
                : "No release entries were found in the changelog."}
            </div>
          )}

          {status === "ready" &&
            filteredReleases.map((release) => (
              <ReleaseEntry
                key={`${release.version}-${release.date}`}
                release={release}
                isCurrent={
                  normalizeVersion(release.version) ===
                  normalizeVersion(packageInfo.version)
                }
                isNew={isUnreleasedForUser(release.version, packageInfo.version)}
                isExpanded={Boolean(expanded[release.version])}
                onToggle={() => toggleRelease(release.version)}
                // 3. Hand the baton from the main modal to the entry
                searchQuery={searchQuery}
              />
            ))}

          <div className="h-4 shrink-0" aria-hidden />
        </div>

        <InstallVersions rows={installRows} />
      </DialogContent>
    </Dialog>
  );
}
